/**
 * Split a multi-item HubSpot deal name into its individual items.
 * Spec: docs/specs_20260715_mobile_tablet_sweep.md §4.5 / plan task 5.3
 *
 * Multi-item deals arrive as one pre-concatenated string:
 *
 *   "1. JBL LSR308 - Studio Monitors - Black - Qty:1 - Excellent [src:505326654674]
 *    & 2. Alesis M1 Active 520 - Studio Monitors - Black - Qty:1 - Excellent [src:505330413783]
 *    & 3. JBL LSR308 - Studio Monitors - Black - Qty:1 - Excellent [src:505330029790]"
 *
 * ~400 characters in a single table cell. No breakpoint fixes that — a cell
 * whose content is intrinsically that long is a rendering problem, not a
 * responsive one (it was clipped at 1440px too, not just on the iPad).
 *
 * PRESENTATION ONLY. This never touches the amount, and Payments is a page
 * where money discipline matters (tasks/lessons.md:119). It splits a display
 * string for display; nothing downstream reads its output.
 *
 * Deliberately conservative: it splits only on the exact "<space>&<space>"
 * separator followed by "<n>. ", which is the shape the pricing pipeline emits
 * (see normaliseIntakeText in the pricing worker, and tasks/lessons.md:68 on
 * this same numbered-list format). Anything it does not recognise — including
 * every single-item deal — comes back as one item, unchanged. A deal name is
 * operator-facing text on a payments screen; guessing at its structure and
 * getting it wrong is worse than leaving it alone.
 *
 * @param {string} dealname
 * @returns {string[]} one entry per item; [] for empty/absent input
 */
export function splitDealItems(dealname) {
  if (!dealname || typeof dealname !== 'string') return []

  const trimmed = dealname.trim()
  if (!trimmed) return []

  // " & " immediately before "2. ", "3. " ... Requires the numbered prefix, so
  // an item whose own text contains " & " (e.g. "Denon UD M31 CD player &
  // receiver") is not split by accident.
  return trimmed.split(/\s+&\s+(?=\d+\.\s)/).map((item) => item.trim()).filter(Boolean)
}
